<template>
    <b-modal
        :visible="show"
        @hidden="$emit('update:show', false)"
        :title="isCreateMode ? '운영경계 추가' : '운영경계 수정'"
        size="lg"
        centered
        hide-footer
        modal-class="boundary-modal-high-z"
    >
        <div class="boundary-edit-form">
            <!-- 경계 정보 -->
            <div class="form-section">
                <h5 class="section-title">운영경계 정보</h5>
                <div class="form-group">
                    <label class="form-label">경계명</label>
                    <input
                        type="text"
                        class="form-control"
                        v-model="formData.operBoundaryName"
                        placeholder="운영경계명 입력"
                    />
                </div>
                <div class="row">
                    <div class="col-md-6">
                        <div class="form-group">
                            <label class="form-label">시작일</label>
                            <div class="date-picker-wrapper">
                                <date-picker
                                    v-model="formData.startDate"
                                    type="date"
                                    format="YYYY-MM-DD"
                                    value-type="format"
                                    placeholder="시작일 선택"
                                    :append-to-body="true"
                                    :popup-style="{ zIndex: 10001 }"
                                ></date-picker>
                            </div>
                        </div>
                    </div>
                    <div class="col-md-6">
                        <div class="form-group">
                            <label class="form-label">종료일</label>
                            <div class="date-picker-wrapper">
                                <date-picker
                                    v-model="formData.endDate"
                                    type="date"
                                    format="YYYY-MM-DD"
                                    value-type="format"
                                    placeholder="종료일 선택"
                                    :append-to-body="true"
                                    :popup-style="{ zIndex: 10001 }"
                                ></date-picker>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Scope 설정 -->
            <div class="form-section">
                <h5 class="section-title">Scope 설정</h5>
                <div class="scope-checkboxes">
                    <div class="form-check">
                        <input
                            type="checkbox"
                            class="form-check-input"
                            id="scope1"
                            :checked="formData.scope1 === 1"
                            @change="updateScope('scope1', $event.target.checked)"
                        />
                        <label class="form-check-label" for="scope1">Scope 1 (직접 배출)</label>
                    </div>
                    <div class="form-check">
                        <input
                            type="checkbox"
                            class="form-check-input"
                            id="scope2"
                            :checked="formData.scope2 === 1"
                            @change="updateScope('scope2', $event.target.checked)"
                        />
                        <label class="form-check-label" for="scope2">Scope 2 (간접 배출)</label>
                    </div>
                    <div class="form-check">
                        <input
                            type="checkbox"
                            class="form-check-input"
                            id="scope3"
                            :checked="formData.scope3 === 1"
                            @change="updateScope('scope3', $event.target.checked)"
                        />
                        <label class="form-check-label" for="scope3">Scope 3 (기타 간접 배출)</label>
                    </div>
                </div>
            </div>

            <!-- 카테고리 선택 -->
            <div class="form-section">
                <h5 class="section-title">배출 카테고리</h5>

                <!-- Scope 1 카테고리 -->
                <div v-if="formData.scope1" class="category-group">
                    <h6 class="category-group-title">Scope 1 (직접 배출)</h6>
                    <div class="category-checkboxes">
                        <label
                            v-for="category in masterCategories.scope1 || []"
                            :key="category.code"
                            class="category-checkbox-item"
                        >
                            <input
                                type="checkbox"
                                :checked="isCategoryIncluded(category)"
                                @change="toggleCategory(category)"
                            />
                            <span>{{ category.name }}</span>
                        </label>
                    </div>
                </div>

                <!-- Scope 2 카테고리 -->
                <div v-if="formData.scope2" class="category-group">
                    <h6 class="category-group-title">Scope 2 (간접 배출)</h6>
                    <div class="category-checkboxes">
                        <label
                            v-for="category in masterCategories.scope2 || []"
                            :key="category.code"
                            class="category-checkbox-item"
                        >
                            <input
                                type="checkbox"
                                :checked="isCategoryIncluded(category)"
                                @change="toggleCategory(category)"
                            />
                            <span>{{ category.name }}</span>
                        </label>
                    </div>
                </div>

                <!-- Scope 3 카테고리 -->
                <div v-if="formData.scope3" class="category-group">
                    <h6 class="category-group-title">Scope 3 (기타 간접 배출)</h6>
                    <div class="category-checkboxes">
                        <label
                            v-for="category in masterCategories.scope3 || []"
                            :key="category.code"
                            class="category-checkbox-item"
                        >
                            <input
                                type="checkbox"
                                :checked="isCategoryIncluded(category)"
                                @change="toggleCategory(category)"
                            />
                            <span>{{ category.name }}</span>
                        </label>
                    </div>
                </div>
            </div>

            <!-- 범위 및 근거 -->
            <div class="form-section">
                <div class="form-group">
                    <label class="form-label">운영 범위</label>
                    <input
                        type="text"
                        class="form-control"
                        v-model="formData.operationalScope"
                        placeholder="운영 범위 설명"
                    />
                </div>
                <div class="form-group">
                    <label class="form-label">범위 설정 근거</label>
                    <textarea
                        class="form-control"
                        v-model="formData.scopeRationale"
                        rows="3"
                        placeholder="범위 설정 근거 입력"
                    ></textarea>
                </div>
                <div class="form-group">
                    <label class="form-label">비고</label>
                    <textarea
                        class="form-control"
                        v-model="formData.remarks"
                        rows="2"
                        placeholder="비고를 입력하세요"
                    ></textarea>
                </div>
            </div>

            <!-- 액션 버튼 -->
            <div class="modal-actions">
                <button class="btn btn-secondary" @click="$emit('update:show', false)">취소</button>
                <button class="btn btn-primary" @click="save" :disabled="saving">
                    <i class="fas fa-spinner fa-spin" v-if="saving"></i>
                    {{ saving ? "저장 중..." : "저장" }}
                </button>
            </div>
        </div>
    </b-modal>
</template>

<script>
import carbonApi from "../../../api";
import { unwrapSuccess, extractErrorMessage } from "../../../utils/api.js";
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";

export default {
    name: "BoundaryOperBoundaryEditModal",
    components: {
        DatePicker,
    },
    props: {
        show: {
            type: Boolean,
            required: true,
        },
        boundaryData: {
            type: Object,
            required: true,
        },
        orgCode: {
            type: String,
            default: null,
        },
        masterCategories: {
            type: Object,
            default: () => ({ scope1: [], scope2: [], scope3: [] }),
        },
    },
    computed: {
        isCreateMode() {
            return !this.boundaryData.operBoundaryIdx;
        },
    },
    data() {
        return {
            saving: false,
            formData: {
                operBoundaryName: "",
                scope1: 0,
                scope2: 0,
                scope3: 0,
                operationalScope: "",
                scopeRationale: "",
                remarks: "",
                startDate: null,
                endDate: null,
                categories: [], // 카테고리 목록
            },
        };
    },
    watch: {
        show(newVal) {
            if (newVal) {
                this.loadFormData();
            }
        },
        boundaryData: {
            handler(newVal, oldVal) {
                if (!this.show) return;
                if (newVal === oldVal) return;
                this.loadFormData();
            },
            deep: false,
        },
    },
    mounted() {
        if (this.show) {
            this.loadFormData();
        }
    },
    methods: {
        updateScope(scopeName, checked) {
            this.$set(this.formData, scopeName, checked ? 1 : 0);
            // Scope 해제 시 해당 Scope의 모든 카테고리를 formData.categories에서 제거
            if (!checked && this.formData.categories && Array.isArray(this.formData.categories)) {
                const scopeType = scopeName;
                const filtered = this.formData.categories.filter((cat) => !cat || cat.scopeType !== scopeType);
                this.$set(this.formData, "categories", filtered);
            }
        },

        loadFormData() {
            if (!this.boundaryData) return;

            // 각 필드를 개별적으로 설정하여 v-model 반응성 유지
            this.$set(this.formData, "operBoundaryName", this.boundaryData.operBoundaryName || "");

            // Scope 설정: 항상 categories에서만 추출 (진실의 원천)
            // boundaryData.scope1/2/3는 무시 (트리 등에서 오는 값과 불일치 가능)
            let scope1 = 0;
            let scope2 = 0;
            let scope3 = 0;

            if (Array.isArray(this.boundaryData.categories) && this.boundaryData.categories.length > 0) {
                const includedCategories = this.boundaryData.categories.filter(
                    (cat) => cat && (cat.included === 1 || cat.included === true || cat.included === "1"),
                );
                const scopeSet = new Set();
                includedCategories.forEach((category) => {
                    if (category.scopeType) scopeSet.add(category.scopeType.toLowerCase());
                });
                if (scopeSet.has("scope1")) scope1 = 1;
                if (scopeSet.has("scope2")) scope2 = 1;
                if (scopeSet.has("scope3")) scope3 = 1;
            }

            this.$set(this.formData, "scope1", scope1);
            this.$set(this.formData, "scope2", scope2);
            this.$set(this.formData, "scope3", scope3);

            this.$set(this.formData, "operationalScope", this.boundaryData.operationalScope || "");
            this.$set(this.formData, "scopeRationale", this.boundaryData.scopeRationale || "");
            this.$set(this.formData, "remarks", this.boundaryData.remarks || "");
            this.$set(this.formData, "startDate", this.boundaryData.startDate || null);
            this.$set(this.formData, "endDate", this.boundaryData.endDate || null);

            // categories 배열 로드 및 included 값 정규화 (boolean -> 1/0)
            const categories = Array.isArray(this.boundaryData.categories)
                ? this.boundaryData.categories
                      .map((cat) => {
                          if (!cat) return null;
                          // included 값을 정규화: boolean true/false -> 1/0
                          const normalizedIncluded =
                              cat.included === true || cat.included === 1 || cat.included === "1" ? 1 : 0;
                          return {
                              ...cat,
                              included: normalizedIncluded,
                          };
                      })
                      .filter(Boolean) // null 제거
                : [];
            this.$set(this.formData, "categories", categories);
        },

        isCategoryIncluded(category) {
            if (!this.formData.categories || !category) return false;

            const cat = this.formData.categories.find(
                (c) => c && c.categoryCode === category.code && c.scopeType === category.scopeType,
            );

            if (!cat) return false;

            // included 값이 boolean(true) 또는 숫자(1) 또는 문자열("1")인지 확인
            return cat.included === true || cat.included === 1 || cat.included === "1";
        },

        toggleCategory(category) {
            if (!this.formData.categories) {
                this.$set(this.formData, "categories", []);
            }

            const existingIndex = this.formData.categories.findIndex(
                (c) => c && c.categoryCode === category.code && c.scopeType === category.scopeType,
            );

            if (existingIndex >= 0) {
                // 토글 - Vue.set 사용으로 반응성 보장
                const currentIncluded = this.formData.categories[existingIndex].included;
                // 현재 값이 true 또는 1이면 0으로, 아니면 1로 변경
                const newIncluded =
                    currentIncluded === true || currentIncluded === 1 || currentIncluded === "1" ? 0 : 1;
                this.$set(this.formData.categories[existingIndex], "included", newIncluded);
            } else {
                // 추가
                this.formData.categories.push({
                    scopeType: category.scopeType,
                    categoryCode: category.code,
                    categoryName: category.name,
                    included: 1,
                });
            }
        },

        async save() {
            this.saving = true;

            try {
                // 1. 운영경계 정보 저장
                const payload = {
                    operBoundaryName: this.formData.operBoundaryName || null,
                    scope1: this.formData.scope1 || 0,
                    scope2: this.formData.scope2 || 0,
                    scope3: this.formData.scope3 || 0,
                    operationalScope: this.formData.operationalScope || null,
                    scopeRationale: this.formData.scopeRationale || "",
                    remarks: this.formData.remarks,
                    startDate: this.formData.startDate || null,
                    endDate: this.formData.endDate || null,
                };

                let operBoundaryIdx;
                if (this.isCreateMode) {
                    // 생성 모드
                    if (!this.orgCode) {
                        throw new Error("조직 코드가 필요합니다.");
                    }
                    const response = await carbonApi.operBoundary.createOperBoundary(this.orgCode, payload);
                    const responseData = unwrapSuccess(response, "운영경계 생성에 실패했습니다.");
                    operBoundaryIdx = responseData?.operBoundaryIdx;

                    this.$bvToast.toast("운영경계가 추가되었습니다.", {
                        variant: "success",
                        title: "추가 완료",
                    });
                } else {
                    // 수정 모드
                    operBoundaryIdx = this.boundaryData.operBoundaryIdx;
                    await unwrapSuccess(
                        await carbonApi.operBoundary.updateOperBoundary(operBoundaryIdx, payload),
                        "운영경계 수정에 실패했습니다.",
                    );

                    this.$bvToast.toast("운영경계가 수정되었습니다.", {
                        variant: "success",
                        title: "수정 완료",
                    });
                }

                // 2. 카테고리 매핑 저장
                // 활성 Scope만 반영: 해제된 Scope의 카테고리는 전송하지 않음
                const activeScopes = new Set();
                if (this.formData.scope1 === 1) activeScopes.add("scope1");
                if (this.formData.scope2 === 1) activeScopes.add("scope2");
                if (this.formData.scope3 === 1) activeScopes.add("scope3");

                const includedCategories = (this.formData.categories || [])
                    .filter((cat) => {
                        if (!cat) return false;
                        const included = cat.included === true || cat.included === 1 || cat.included === "1";
                        const scopeKey = (cat.scopeType || "").toLowerCase();
                        return activeScopes.has(scopeKey) && included;
                    })
                    .map((cat) => ({
                        categoryCode: cat.categoryCode,
                        categoryName: cat.categoryName,
                        scopeType: (cat.scopeType || "").toUpperCase(),
                        included: true,
                    }));

                if (operBoundaryIdx && includedCategories.length > 0) {
                    await unwrapSuccess(
                        await carbonApi.operBoundary.saveCategories(operBoundaryIdx, {
                            categories: includedCategories,
                        }),
                        "카테고리 매핑 저장에 실패했습니다.",
                    );
                }

                this.$emit("saved", { operBoundaryIdx });
                this.$emit("update:show", false);
            } catch (error) {
                console.error("[BoundaryOperBoundaryEditModal] 운영경계 저장 실패:", error);

                const errorMsg = extractErrorMessage(error, "운영경계 저장에 실패했습니다.");
                this.$bvToast.toast(errorMsg, {
                    variant: "danger",
                    title: "저장 실패",
                });
            } finally {
                this.saving = false;
            }
        },
    },
};
</script>

<style scoped>
.boundary-edit-form {
    padding: 10px 0;
}

.form-section {
    margin-bottom: 24px;
    padding-bottom: 24px;
    border-bottom: 1px solid #e2e8f0;
}

.form-section:last-of-type {
    border-bottom: none;
}

.section-title {
    font-size: 14px;
    font-weight: 600;
    color: #1e293b;
    margin-bottom: 16px;
}

.form-group {
    margin-bottom: 16px;
}

.form-label {
    font-size: 12px;
    font-weight: 600;
    color: #475569;
    margin-bottom: 6px;
    display: block;
}

.form-control {
    font-size: 13px;
    padding: 8px 12px;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
}

.form-control:focus {
    border-color: #52b788;
    box-shadow: 0 0 0 3px rgba(82, 183, 136, 0.1);
}

.scope-checkboxes {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.form-check {
    display: flex;
    align-items: center;
    gap: 8px;
}

.form-check-input {
    width: 18px;
    height: 18px;
    cursor: pointer;
}

.form-check-label {
    font-size: 13px;
    font-weight: 500;
    color: #334155;
    cursor: pointer;
    margin-bottom: 0;
}

.modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 24px;
    padding-top: 24px;
    border-top: 1px solid #e2e8f0;
}

.btn {
    padding: 8px 16px;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    border: none;
}

.btn-secondary {
    background: #e2e8f0;
    color: #475569;
}

.btn-secondary:hover {
    background: #cbd5e1;
}

.btn-primary {
    background: #52b788;
    color: white;
}

.btn-primary:hover:not(:disabled) {
    background: #2d8659;
}

.btn-primary:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.category-group {
    margin-bottom: 18px;
    padding: 12px;
    background: #f8fafc;
    border-radius: 6px;
}

.category-group-title {
    font-size: 12px;
    font-weight: 600;
    color: #334155;
    margin-bottom: 10px;
}

.category-checkboxes {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 10px;
}

.category-checkbox-item {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: #475569;
    cursor: pointer;
    margin-bottom: 0;
}

.category-checkbox-item input[type="checkbox"] {
    width: 16px;
    height: 16px;
    cursor: pointer;
}

.category-checkbox-item:hover {
    color: #1e293b;
}

.date-picker-wrapper {
    width: 100%;
}

/* DatePicker popup z-index 조정 */
.date-picker-wrapper >>> .mx-datepicker-popup {
    z-index: 10001 !important;
}

/* DatePicker input 클릭 가능하도록 */
.date-picker-wrapper >>> .mx-input-wrapper {
    cursor: pointer;
    pointer-events: auto;
}

.date-picker-wrapper >>> .mx-input {
    cursor: pointer;
    pointer-events: auto;
}

/* DatePicker 아이콘 클릭 가능하도록 */
.date-picker-wrapper >>> .mx-input-wrapper .mx-calendar-icon {
    cursor: pointer;
    pointer-events: auto;
}
</style>
